import { Directive, input } from '@angular/core';
import type { FormlyFieldConfig } from '@ngx-formly/core';

/**
 * K8 (reuse): every read display resolved via `ReadonlyDisplayInputs`
 * (`readonly-field-row.component.ts`, `NgComponentOutletInputs`) redeclared the same two inputs
 * byte-for-byte. Extend this instead of declaring `field`/`value` locally.
 *
 * `@Directive()` is required here, not optional style: `ComponentRef.setInput` (what
 * `NgComponentOutlet` — and `TestBed`'s `componentRef.setInput` — actually calls) validates the
 * input name against the compiled component's OWN `ɵcmp.inputs`, and Angular's compiler only
 * merges a signal input from an ANCESTOR class into that map when the ancestor itself carries
 * directive metadata (confirmed empirically: an undecorated plain base throws NG0303 "Can't set
 * value of the 'field' input" from every display's own spec, even for a same-class
 * `fixture.componentRef.setInput` with no `NgComponentOutlet` involved at all). `FieldType`
 * (`@ngx-formly/core`) is decorated for the exact same reason.
 *
 * Angular's `NgComponentOutletInputs` binds to the dynamically-resolved component BY NAME at
 * runtime, with no compile-time check that a display's own inputs match `ReadonlyDisplayInputs`
 * — sharing this base doesn't add that check, but it does mean a new display can no longer get
 * the name or the type of either input wrong by hand-copying.
 */
@Directive()
export abstract class ReadonlyDisplayBase {
  readonly field = input.required<FormlyFieldConfig>();
  /**
   * Resolved by the wrapper (`readonlyFieldValue`, or a presence variant like
   * `readonlyPresenceValue` — see the extending component's own docs), not read from the control
   * here.
   */
  readonly value = input<unknown>();
}
